import React, { useState, useCallback, forwardRef, useEffect, useImperativeHandle } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import { WcnTextField } from '@/components';
import { useTranslation } from 'react-i18next';
import NewWcnTable, {
    DataTableColumn,
    DataTablePagination,
    DataTableSorting
} from '@/components/datatable/NewWcnTable.tsx';
import { rfidTagService } from '@/services';
import type { RfidTag } from '@/services';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';

export interface RfidTagsTableRef {
    load: () => void;
}

const RfidTagsTable = forwardRef<RfidTagsTableRef, {}>((props, ref) => {
    const { t } = useTranslation();
    const [tags, setTags] = useState<RfidTag[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState<string>('id');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const payload = {
                page: page + 1,
                itemsPerPage: rowsPerPage,
                sortBy: sortBy ? [sortBy] : [],
                sortDesc: sortBy ? [sortDir === 'desc'] : [],
                query: query || ''
            };
            const response = await rfidTagService.loadRfidTags(payload);

            setTags(response.items);
            setTotal(response.total);
        } catch (error) {
            console.error('Failed to fetch tags:', error);
            setTags([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, query, sortBy, sortDir]);

    useEffect(() => {
        // Initial load
        load();

        const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, load);

        return () => {
            unsubscribeReload();
        };
    }, [load]);

    useImperativeHandle(ref, () => ({
        load
    }));

    const handleClearQuery = useCallback(() => {
        setQuery('');
        setPage(0);
    }, []);

    const sorting: DataTableSorting = {
        defaultSortBy: 'id',
        sortBy,
        sortDirection: sortDir,
        onSortChange: (field, dir) => {
            setSortBy(field);
            setSortDir(dir);
            setPage(0);
        }
    };

    const columns: DataTableColumn<RfidTag>[] = [
        {
            text: t('id'),
            value: 'id',
            width: 150
        },
        {
            text: t('product'),
            value: 'product.name',
            width: 200,
            render: (row) => row.product?.name
        }
    ];

    const pagination: DataTablePagination = {
        count: total,
        page,
        rowsPerPage,
        onPageChange: (_event, newPage) => setPage(newPage),
        onRowsPerPageChange: (e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
        },
        rowsPerPageOptions: [5, 10, 15]
    };

    return (
        <Box>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <WcnTextField
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(0);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                load();
                            }
                        }}
                        label={t('search')}
                        size="small"
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {query && (
                                        <IconButton size="small" onClick={handleClearQuery}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton size="small" onClick={() => load()} color="inherit" title={t('search')}>
                                        <SearchIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )
                        }}
                    />
                </Grid>
            </Grid>
            <NewWcnTable<RfidTag>
                data={tags}
                loading={loading}
                columns={columns}
                pagination={pagination}
                sorting={sorting}
                noDataMessage={t('noData')}
                size="small"
            />
        </Box>
    );
});

RfidTagsTable.displayName = 'RfidTagsTable';

export default RfidTagsTable;
